import type {Result} from "@khanacademy/wonder-blocks-data";

import type {ColumnName} from "../column-names.ts";
import ConversationsView from "../conversations-view.tsx";
import TableView from "../table-view.tsx";

import type {
    guideTranscripts_aiGuideThreads_threads as AIGuideThreads,
    guideTranscriptsType,
} from "./__graphql-types__/guideTranscripts.ts";
import {GuideTranscriptsError} from "./guide-transcripts-error.tsx";

type Relationship = "SELF" | "COACH" | "DISTRICT_ADMIN" | "OTHER";

interface Props {
    /**
     * The query result containing status and data
     */
    result: Result<guideTranscriptsType["response"]>;
    /**
     * Array of AI guide threads to display
     */
    threads: Array<AIGuideThreads>;
    /**
     * Show the new table view instead of the conversations view
     */
    renderTableView?: boolean;
    /**
     * For the conversations view, if a conversation is flagged, render the
     * notice in the first column, under the Chat Name
     */
    showFlaggedNotice?: boolean;
    /**
     * The columns to display in the table. Defaults to all columns.
     * The values are the keys of the ThreadType object. Order matters.
     */
    visibleColumns?: Array<ColumnName>;
    /**
     * The relationship between the viewer and the profile owner. Used for
     * analytics purposes
     */
    relationship: Relationship;
    /**
     * URL path to the user's profile, needed for conversation links
     */
    profilePath?: string;
    /**
     * Whether filters are currently applied (non-default values).
     * Used to determine which empty state message to show.
     */
    hasAppliedFilters?: boolean;
    /**
     * Current page number for pagination
     */
    currentPage?: number;
    /**
     * Page change handler for pagination
     */
    onPageChange?: (nextPage: number) => void;
    /**
     * Whether there are more pages available
     */
    hasMorePages?: boolean;
    /**
     * Whether the current page is the Tutor Me page. Affects the page size.
     */
    isTutorMePage?: boolean;
}

/**
 * Renders the loaded content based on the result of the query.
 */
const GuideTranscriptsContent = ({
    result,
    threads,
    renderTableView,
    showFlaggedNotice,
    visibleColumns,
    relationship,
    profilePath,
    hasAppliedFilters,
    currentPage,
    onPageChange,
    hasMorePages,
    isTutorMePage,
}: Props): React.ReactNode => {
    if (result.status === "error") {
        return <GuideTranscriptsError />;
    }

    if (renderTableView) {
        return (
            <TableView
                threads={threads}
                showFlaggedNotice={showFlaggedNotice}
                visibleColumns={visibleColumns}
                relationship={relationship}
                hasAppliedFilters={hasAppliedFilters}
                currentPage={currentPage}
                onPageChange={onPageChange}
                hasMorePages={hasMorePages}
                isTutorMePage={isTutorMePage}
                isLoadingOrNoData={
                    result.status === "loading" || result.status === "no-data"
                }
            />
        );
    }

    if (profilePath) {
        // Classic view needs profile path for thread link
        return (
            <ConversationsView
                threads={threads}
                profilePath={profilePath}
                pageSize={10}
                currentPage={currentPage}
                onPageChange={onPageChange}
                hasMorePages={hasMorePages}
            />
        );
    }

    // Fallback for classroom view without profile path
    return null;
};

export {GuideTranscriptsContent};
